import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiJson, assetUrl } from "../utils/api";
import { canAccessSingleDating } from "../constants/maritalStatus";
import VerificationBadges from "./VerificationBadges";

let _cacheKey = "";
let _cacheData = null;
let _cacheAt = 0;
const CACHE_MS = 60_000;

async function fetchRecommendUsers(userId, limit) {
  const key = `${userId || "guest"}:${limit}`;
  if (_cacheKey === key && _cacheData && Date.now() - _cacheAt < CACHE_MS) {
    return _cacheData;
  }
  const params = new URLSearchParams();
  params.set("category", "recommended");
  params.set("limit", String(limit));
  if (userId) params.set("current_user_id", userId);
  const { data } = await apiJson(`/api/public/users?${params}`);
  const list = Array.isArray(data) ? data : [];
  _cacheKey = key;
  _cacheData = list;
  _cacheAt = Date.now();
  return list;
}

/**
 * 高流量页「推荐对象」
 * layout:
 * - "sidebar"：仅桌面右侧样式（父级用 hidden lg:block）
 * - "strip"：仅手机横滑（父级用 lg:hidden）
 * - "both"：组件内同时输出两侧（单次挂载时用）
 */
export default function RecommendPeopleRail({
  layout = "both",
  title = "推荐对象",
  subtitle = "玩的时候也可以点进去看看",
  limit = 8,
  className = "",
}) {
  const userId = localStorage.getItem("user_id");
  const marital = localStorage.getItem("marital_status") || "";
  const showDating = canAccessSingleDating(marital);

  const [items, setItems] = useState(() =>
    _cacheKey.startsWith(`${userId || "guest"}:`) ? _cacheData || [] : []
  );
  const [loading, setLoading] = useState(!_cacheData);

  useEffect(() => {
    if (!showDating) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchRecommendUsers(userId, limit)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, limit, showDating]);

  if (!showDating) return null;

  const list = (items || []).slice(0, limit);

  const sidebar = (
    <div className="w-56 xl:w-64 shrink-0">
      <div className="sticky top-24 rounded-2xl border border-rose-100 bg-white/95 shadow-sm p-4 space-y-3 max-h-[calc(100vh-7rem)] overflow-y-auto">
        <div>
          <h3 className="font-bold text-gray-800 text-sm">{title}</h3>
          <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">{subtitle}</p>
        </div>
        {loading ? (
          <p className="text-xs text-gray-400 py-6 text-center">加载中…</p>
        ) : list.length === 0 ? (
          <p className="text-xs text-gray-400 py-4 text-center">暂无推荐</p>
        ) : (
          <ul className="space-y-2.5">
            {list.map((u) => (
              <li key={u.id}>
                <Link
                  to={`/user/${u.id}`}
                  className="flex gap-2.5 items-center rounded-xl p-2 hover:bg-rose-50 transition border border-transparent hover:border-rose-100"
                >
                  <img
                    src={
                      u.avatar
                        ? assetUrl(u.avatar)
                        : "https://via.placeholder.com/48"
                    }
                    alt=""
                    className="w-11 h-11 rounded-full object-cover shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {u.name || "会员"}
                    </p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {u.age ? `${u.age}岁` : "年龄未填"}
                      {u.city ? ` · ${u.city}` : ""}
                    </p>
                    <VerificationBadges
                      verification={u.verification}
                      userId={u.id}
                      variant="compact"
                      className="mt-0.5"
                    />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
        <Link
          to="/discover"
          className="block text-center text-xs text-rose-600 font-medium pt-1 hover:underline"
        >
          去寻找对象 →
        </Link>
      </div>
    </div>
  );

  const strip = (
    <div className="rounded-2xl border border-rose-100 bg-white/95 p-4 space-y-3">
      <div className="flex items-end justify-between gap-2">
        <div>
          <h3 className="font-bold text-gray-800 text-sm">{title}</h3>
          <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>
        </div>
        <Link to="/discover" className="text-xs text-rose-600 shrink-0">
          更多
        </Link>
      </div>
      {loading ? (
        <p className="text-xs text-gray-400 py-4 text-center">加载中…</p>
      ) : list.length === 0 ? (
        <p className="text-xs text-gray-400 py-3 text-center">暂无推荐</p>
      ) : (
        <div className="-mx-1 overflow-x-auto pb-1">
          <ul className="flex gap-3 px-1 min-w-min">
            {list.map((u) => (
              <li key={u.id} className="w-28 shrink-0">
                <Link
                  to={`/user/${u.id}`}
                  className="block rounded-xl border border-rose-50 bg-rose-50/40 p-2 text-center hover:border-rose-200 transition"
                >
                  <img
                    src={
                      u.avatar
                        ? assetUrl(u.avatar)
                        : "https://via.placeholder.com/64"
                    }
                    alt=""
                    className="w-14 h-14 rounded-full object-cover mx-auto"
                  />
                  <p className="text-xs font-medium text-gray-800 mt-2 truncate">
                    {u.name || "会员"}
                  </p>
                  <p className="text-[10px] text-gray-500 truncate">
                    {u.age ? `${u.age}岁` : ""}
                    {u.city ? ` · ${u.city}` : ""}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  if (layout === "sidebar") {
    return <aside className={className}>{sidebar}</aside>;
  }
  if (layout === "strip") {
    return <aside className={className}>{strip}</aside>;
  }

  return (
    <aside className={className}>
      <div className="hidden lg:block">{sidebar}</div>
      <div className="lg:hidden">{strip}</div>
    </aside>
  );
}
